import React, { useEffect, useMemo, useCallback } from "react";
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import useFlowData from "./hooks/use-flow-data.js";
import useStore from "./store";
import {
  useEventEmitterContext,
  EventEmitterType,
  AgentSaveType,
} from "../../context/event-emitter.js";
import { debounce } from "../../lib/debounce";
import useNodesInteractions from "./hooks/use-nodes-interactions";
import useEdgesInteractions from "./hooks/use-edges-interactions.js";

// Custom nodes
import Start from "./nodes/Start/index.js";
import Conversation from "./nodes/Conversation/index.js";
import Greeting from "./nodes/Greeting/index.js";
import HumanAgentHandoff from "./nodes/HumanAgentHandoff/index.js";
import LanguageDetection from "./nodes/LanguageDetection/index.js";
import HangUp from "./nodes/HangUp/index.js";
import Sequence from "./nodes/Sequence/index.js";

import CustomEdge from "./edges/custom-edge.js";
import { CUSTOM_NODE } from "./types";

const nodeTypes = {
  [CUSTOM_NODE.Start]: Start,
  [CUSTOM_NODE.Conversation]: Conversation,
  [CUSTOM_NODE.Greeting]: Greeting,
  [CUSTOM_NODE.HumanAgentHandoff]: HumanAgentHandoff,
  [CUSTOM_NODE.LanguageDetection]: LanguageDetection,
  [CUSTOM_NODE.HangUp]: HangUp,
  [CUSTOM_NODE.Sequence]: Sequence,
};

const edgeTypes = {
  custom: CustomEdge,
};

const Workflow = ({ isLoading, nodesData, edgesData, onNodesChange: parentNodesChange, onEdgesChange: parentEdgesChange }) => {
  const { fitView, toObject } = useReactFlow();
  const { getLayoutedElements } = useNodesInteractions();
  const { handleEdgeEnter, handleEdgeLeave } = useEdgesInteractions();

  const agentSetting = useStore((state) => state.agentSetting);
  const setNodes = useStore((state) => state.setNodes);
  const setEdges = useStore((state) => state.setEdges);

  const { eventEmitter } = useEventEmitterContext();

  // Subscription for external save events
  eventEmitter?.useSubscription((v) => {
    if (v.type === EventEmitterType.AgentSave) {
      debouncedSave(v.payload);
    }
  });

  const handleSave = useCallback(
    (saveType) => {
      if (isLoading) return;
      const obj = toObject();
      console.log("🔄 AutoSave", saveType, obj, agentSetting);
    },
    [agentSetting, isLoading, toObject]
  );

  const debouncedSave = useMemo(() => debounce(handleSave, 500), [handleSave]);

  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onConnect,
  } = useFlowData();

  // ✅ Keep parent component in sync with live state
  useEffect(() => {
    if (parentNodesChange) parentNodesChange(nodes);
  }, [nodes, parentNodesChange]);

  useEffect(() => {
    if (parentEdgesChange) parentEdgesChange(edges);
  }, [edges, parentEdgesChange]);

  useEffect(() => {
    if (nodesData.length) {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(nodesData, edgesData);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    }

    fitView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodesData, edgesData]);

  useEffect(() => {
    return () => {
      console.log("ReactFlow unmounted, clearing state");
      setNodes([]);
      setEdges([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      id="react-container"
      className="flex-1 w-full h-full overflow-hidden"
    >
      <ReactFlow
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView
        fitViewOptions={{ maxZoom: 1 }}
        attributionPosition="top-right"
        maxZoom={1.3}
        deleteKeyCode={["Delete", "Backspace"]}
        defaultEdgeOptions={{ type: "custom" }}
        onEdgeMouseEnter={handleEdgeEnter}
        onEdgeMouseLeave={handleEdgeLeave}
      >
        <Controls />
        <MiniMap zoomable pannable />
        <Background
          bgColor="#F0F2F5"
          variant={BackgroundVariant.Dots}
          gap={12}
          size={1}
        />
      </ReactFlow>
    </div>
  );
};

export default Workflow;
